import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Input, message, Spin, Select, Alert } from 'antd';
import { PrinterOutlined, CheckCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getTemplate } from './docTemplates';
import './SignDocPage.css';

const API = import.meta.env.VITE_API_BASE || '/api';
const th = () => ({ Authorization: 'Bearer ' + localStorage.getItem('ar_token') });

export default function SignDocPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderNos = searchParams.get('order_nos')?.split(',') || [];
  // 根据来源决定返回路径：自提签收 → 返回自提签收页，否则返回到货签收页
  const from = searchParams.get('from');
  const isSelfPickup = from === 'self-pickup';
  const backPath = isSelfPickup ? '/customer/self-pickup' : '/customer/arrival-sign';
  const [loading, setLoading] = useState(true);
  const [docData, setDocData] = useState([]);
  const [signers, setSigners] = useState([]);
  const [selectedSigner, setSelectedSigner] = useState(null);
  const [remark, setRemark] = useState('');
  const [signing, setSigning] = useState(false);

  // Determine template from first doc's warehouse
  const template = (() => {
    if (docData.length > 0) {
      const wh = docData[0]['仓库'] || '';
      return getTemplate(wh);
    }
    return getTemplate(null);
  })();

  useEffect(() => {
    if (!isSelfPickup) {
      axios.get(API + '/customer/signers', { headers: th() }).then(r => {
        // 签字人：仅需 is_active + 有爱签认证账号 + 委托书已审核通过
        const active = (r.data.data || []).filter(s => s.is_active && s.asign_account && s.letter_auth_status === 'passed');
        setSigners(active);
        if (active.length) setSelectedSigner(active[0].id);
      });
    }
    axios.get(API + '/arrival/sign-doc', { params: { order_nos: orderNos.join(',') }, headers: th() }).then(r => {
      if (r.data.success) setDocData(r.data.docs || []);
    }).finally(() => setLoading(false));
  }, []);

  const handleSign = async () => {
    if (!isSelfPickup && !selectedSigner) return message.warning('请选择签字人');
    setSigning(true);
    try {
      const r = isSelfPickup
        ? await axios.post(API + '/arrival/sign-selfpickup', {
            order_nos: orderNos, remark,
          }, { headers: th() })
        : await axios.post(API + '/arrival/sign-asign', {
            order_nos: orderNos, signer_id: selectedSigner, remark,
          }, { headers: th() });
      if (r.data.success) {
        message.success('签收成功！' + r.data.message);
        navigate(backPath, { state: { fromSignDoc: Date.now() } });
      } else {
        message.error(r.data.message || '签收失败');
      }
    } catch {
      message.error('签收失败');
    }
    setSigning(false);
  };

  if (loading) return <Spin style={{ display: 'block', margin: '30vh auto' }} size="large" />;

  const tdBase = { border: '1px solid #d9d9d9', padding: '2px 3px', fontSize: 10,
    whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' };

  const isTwoColumn = template.infoLayout === 'twoColumn';

  return (
    <div style={{ width: '100%' }}>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(backPath, { state: { fromSignDoc: Date.now() } })} style={{ marginBottom: 16 }}>返回</Button>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {isSelfPickup ? (
          <Alert type="info" showIcon message="自提签收：不选签收人，仅盖客户公司章" />
        ) : (
          <>
            {signers.length === 0 && <Alert type="error" showIcon message="没有可用的签字人" />}
            <Select placeholder="签字人" value={selectedSigner} onChange={setSelectedSigner} style={{ width: 130 }}
              options={signers.map(s => ({ label: s.signer_name, value: s.id }))} />
          </>
        )}
        <Button type="primary" icon={<CheckCircleOutlined />} loading={signing}
          disabled={!isSelfPickup && !signers.length} onClick={handleSign}
          style={{ marginLeft: '8em' }}>
          {isSelfPickup ? '自提签收' : '电子回签'}
        </Button>
        <Button icon={<PrinterOutlined />} onClick={() => window.print()}>打印</Button>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Input placeholder="签收备注" value={remark} onChange={e => setRemark(e.target.value)} style={{ width: '100%' }} />
      </div>

      {docData.map((doc, i) => (
        <div key={i} className="sign-doc" style={{ background: '#fff', padding: 12, marginBottom: 16, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          <h2 style={{ textAlign: 'center', marginBottom: 2, fontSize: 16 }}>{template.title}</h2>
          {template.subtitle && (() => {
            const idx = template.subtitle.indexOf('：');
            const bold = idx > 0 ? template.subtitle.slice(0, idx + 1) : '';
            const rest = idx > 0 ? template.subtitle.slice(idx + 1) : template.subtitle;
            return (
              <p style={{ textAlign: 'left', paddingLeft: '33.33%', fontSize: 12, marginBottom: 8, color: '#555' }}>
                <b>{bold}</b>{rest}
              </p>
            );
          })()}

          {/* Two-column info layout (成都模板) */}
          {isTwoColumn ? (
            <div style={{ display: 'flex', gap: 24, marginBottom: 6 }}>
              {/* Left column */}
              <div style={{ flex: 1, fontSize: 12 }}>
                {(template.infoLeft || []).map((f, fi) => {
                  const val = f.format ? f.format(doc) : (doc[f.field] || f.default || '');
                  return (
                    <div key={fi} style={{ marginBottom: 2 }}>
                      <b>{f.label}：</b>
                      <span style={{ wordBreak: 'break-all' }}>{val}</span>
                    </div>
                  );
                })}
              </div>
              {/* Right column */}
              <div style={{ flex: 1, fontSize: 12 }}>
                {(template.infoRight || []).map((f, fi) => {
                  const val = doc[f.field] || f.default || '';
                  return (
                    <div key={fi} style={{ marginBottom: 2 }}>
                      <b>{f.label}：</b>
                      <span style={{ wordBreak: 'break-all' }}>{val}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              {/* Table header rows (上海模板) - 用 table 确保列对齐 */}
              <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 12, marginBottom: 8 }}>
                <tbody>
                  {template.headerRows.map((row, ri) => {
                    const fields = row.fullWidth ? row.fields : row;
                    return (
                      <tr key={ri}>
                        {fields.map((f, fi) => {
                          const val = doc[f.field] || f.default || '';
                          return (
                            <td key={fi} style={{ width: '33.33%' }}>
                              {f.label ? <b>{f.label}：</b> : null}
                              <span style={{ wordBreak: 'break-all' }}>{val}</span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {/* Items table - overflow scroll on small screens */}
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, tableLayout: 'fixed', minWidth: isTwoColumn ? 700 : 1000 }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  {template.tableColumns.map((col, ci) => (
                    <th key={ci} style={{ ...tdBase, fontWeight: 600, fontSize: 10, textAlign: 'center' }}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(doc.items || []).map((item, j) => (
                  <tr key={j}>
                    {template.tableColumns.map((col, ci) => {
                      if (col.field === null) {
                        return (
                          <td key={ci} style={{ ...tdBase, textAlign: 'center' }}>
                            {ci === 0 ? j + 1 : ''}
                          </td>
                        );
                      }
                      const v = item[col.field];
                      const isNum = col.field === '包装数量' || col.field === '明细数量';
                      return (
                        <td key={ci} style={{ ...tdBase, textAlign: isNum ? 'right' : 'left' }}>
                          {v != null ? String(v) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer rows (成都模板) */}
          {isTwoColumn && template.footerRows && (
            <div style={{ marginTop: 6 }}>
              {template.footerRows.map((fRow, ri) => (
                <div key={ri} style={{ display: 'flex', gap: 12, marginBottom: 2, fontSize: 11 }}>
                  {fRow.map((f, fi) => {
                    const val = doc[f.field] || f.default || '';
                    return (
                      <div key={fi} style={{ flex: 1, minWidth: 0 }}>
                        <b>{f.label}：</b>
                        <span style={{ wordBreak: 'break-all' }}>{val}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Seal area */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 80, marginTop: 16, paddingBottom: 4 }}>
            <div style={{ width: 100, textAlign: 'center', borderBottom: '0.5px solid #999', paddingBottom: 4 }}>
              <span style={{ fontSize: 11, color: '#888' }}>{template.sealArea.left}</span>
            </div>
            <div style={{ width: 100, textAlign: 'center', borderBottom: '0.5px solid #999', paddingBottom: 4 }}>
              <span style={{ fontSize: 11, color: '#888' }}>{template.sealArea.right}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
